import { ArrowRight } from '@tencent/odc-iconpack';
import { ConfigProvider, HoverFill } from '@tencent/one-design-next';
import classNames from 'classnames';
import { Skeleton } from 'odc-shared/components';
import { useLocale } from 'odc-shared/i18n';
import { useCallback, useEffect, useRef, useState } from 'react';

import { CommPlatform } from '@src/api/generated/data-contracts';
import { DocNavRsp, IArticle } from '@src/types/support.type';

import './style.scss';

interface MenuProps {
  navData: DocNavRsp;
  defaultExpandAll?: boolean;
  defaultExpandedIds?: string[];
  activeId?: string;
  platform: CommPlatform;
  // 目录数据是否是当前语言那一份；false 时 navData 为空（上层不给旧语言的名字），此时渲染骨架
  ready?: boolean;
  setPid: (pid: string) => void;
}

/**
 * 递归收集所有有 subList 的节点 aid
 */
const collectExpandableIds = (items: IArticle[]): string[] => {
  const ids: string[] = [];
  const traverse = (list: IArticle[]) => {
    list.forEach(item => {
      if (item.subList && item.subList.length > 0) {
        if (item.aid) {
          ids.push(item.aid);
        }
        traverse(item.subList);
      }
    });
  };
  traverse(items);
  return ids;
};

/**
 * 收集某个节点及其所有子孙中可展开的 id
 */
const collectDescendantExpandableIds = (item: IArticle): string[] => {
  const ids: string[] = [];
  const traverse = (node: IArticle) => {
    if (node.subList && node.subList.length > 0) {
      if (node.aid) ids.push(node.aid);
      node.subList.forEach(traverse);
    }
  };
  traverse(item);
  return ids;
};

/**
 * 在树中找到目标 aid 所在的同级列表
 */
const findSiblingList = (items: IArticle[], aid: string): IArticle[] | null => {
  for (const item of items) {
    if (item.aid === aid) return items;
    if (item.subList && item.subList.length > 0) {
      const found = findSiblingList(item.subList, aid);
      if (found) return found;
    }
  }
  return null;
};

/**
 * 收集从根到目标 aid 路径上所有祖先的 aid（含目标自身）
 */
const collectAncestorIds = (items: IArticle[], targetAid: string): string[] => {
  const path: string[] = [];
  const find = (list: IArticle[], ancestors: string[]): boolean => {
    for (const item of list) {
      const currentPath = item.aid ? [...ancestors, item.aid] : ancestors;
      if (item.aid === targetAid) {
        path.push(...currentPath);
        return true;
      }
      if (item.subList && item.subList.length > 0) {
        if (find(item.subList, currentPath)) return true;
      }
    }
    return false;
  };
  find(items, []);
  return path;
};

interface ArticleMenuSubProps {
  item: IArticle;
  index: number;
  depth?: number;
  expandedIds: Array<string>;
  onToggle: (aid: string) => void;
  setPid: (pid: string) => void;
  activeId: string;
}

function ArticleMenuSub(props: ArticleMenuSubProps) {
  const { item, index, depth = 0, expandedIds, onToggle, activeId, setPid } = props;

  if (item.subList && item.subList.length > 0) {
    const isExpanded = expandedIds.includes(item.aid || '');

    return (
      <div
        key={`odc-docs-menu_${item.name}`}
        className="menu-category"
      >
        <HoverFill
          key={`odc-sublist-item_${index}`}
          bgClassName="rounded-md"
          hoverColor="rgba(0, 0, 0, 0.03)"
          activeColor="rgba(0, 0, 0, 0.05)"
          bgStyle={{ borderRadius: 8 }}
        >
          <div
            className={classNames('menu-category-title', { 'title-top': depth === 0 })}
            onClick={() => onToggle(item.aid as string)}
          >
            {item.name}
            <ArrowRight
              className={classNames('arrow-icon', { 'arrow-expanded': isExpanded })}
            />
          </div>
        </HoverFill>
        <div
          className={classNames('menu-subcategory-list', {
            'indent-list': depth > 0,
            'open': isExpanded,
          })}
        >
          {item.subList.map((subItem, subIndex) => (
            <ArticleMenuSub
              key={subItem.aid}
              activeId={activeId}
              item={subItem}
              index={subIndex}
              depth={depth + 1}
              expandedIds={expandedIds}
              onToggle={onToggle}
              setPid={setPid}
            />
          ))}
        </div>
      </div>
    );
  }

  if (!item.article) return null;

  const articleId = item.aid || '';
  const isActive = articleId === activeId;

  return (
    <HoverFill
      key={`odc-sublist-item_${index}`}
      bgClassName="rounded-md"
      hoverColor="rgba(0, 0, 0, 0.03)"
      activeColor="rgba(0, 0, 0, 0.05)"
      bgStyle={{ borderRadius: 8 }}
    >
      <div
        onClick={() => {
          setPid(articleId);
        }}
        title={item.name}
        key={item.name}
        className={classNames('menu-subcategory-item', {
          active: isActive,
        })}
      >
        {item.name}
      </div>
    </HoverFill>
  );
}

export default function Menu(props: MenuProps) {
  const { navData, defaultExpandAll = false, defaultExpandedIds, activeId, setPid, platform, ready = true } = props;
  const { t } = useLocale('helpCenter');

  // 存储所有展开的节点 aid；初始值直接取默认展开集，避免首帧"先全收起、再展开"地闪一下
  const [expandedIds, setExpandedIds] = useState<string[]>(() => defaultExpandedIds || []);

  // 默认展开集变化时在渲染期合并（放 effect 会先提交一帧"全收起"，菜单跳一下）
  const [lastDefaultExpandedIds, setLastDefaultExpandedIds] = useState(defaultExpandedIds);
  if (lastDefaultExpandedIds !== defaultExpandedIds) {
    setLastDefaultExpandedIds(defaultExpandedIds);
    setExpandedIds(prev => Array.from(new Set([...prev, ...(defaultExpandedIds || [])])));
  }

  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setExpandedIds([]);
  }, [platform]);
  /**
   * 根据 defaultExpandAll 决定是否全部展开
   */
  useEffect(() => {
    if (defaultExpandAll) {
      const allIds = collectExpandableIds(navData);
      setExpandedIds(allIds);
    }
  }, [navData, defaultExpandAll]);

  /**
   * activeId 变动时，自动展开其祖先路径 + 滚动到 active 元素
   */
  useEffect(() => {
    if (!activeId || !menuRef.current) return;

    // 确保 activeId 对应的祖先链都展开
    const ancestorIds = collectAncestorIds(navData, activeId);
    if (ancestorIds.length > 0) {
      setExpandedIds(prev => {
        return Array.from(new Set([...prev, ...ancestorIds]));
      });
    }

    const timer = setTimeout(() => {
      const activeEl = menuRef.current?.querySelector('.menu-subcategory-item.active');
      activeEl?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 50);
    return () => clearTimeout(timer);
  }, [activeId, navData]);

  /**
   * 切换某个节点的展开状态，排他
   *
   * 规则：
   * 1. 关闭节点时：同时关闭该节点下所有子孙的展开状态
   * 2. 展开节点时：先关闭同级兄弟及其子孙的展开状态，再展开当前节点
   * 3. 不影响不同层级：父级和非同级节点的展开状态保持不变
   */
  const handleToggle = useCallback((aid: string) => {
    setExpandedIds(prev => {
      const newSet = new Set(prev);

      if (newSet.has(aid)) {
        // === 关闭 ===
        // 找到该节点，移除自身及所有子孙的展开状态
        const siblings = findSiblingList(navData, aid);
        const target = siblings?.find(item => item.aid === aid);
        if (target) {
          collectDescendantExpandableIds(target).forEach(id => newSet.delete(id));
        } else {
          newSet.delete(aid);
        }
      } else {
        // === 展开 ===
        // 找到同级列表，关闭所有兄弟及其子孙
        const siblings = findSiblingList(navData, aid);
        if (siblings) {
          siblings.forEach(sibling => {
            if (sibling.aid !== aid) {
              collectDescendantExpandableIds(sibling).forEach(id => newSet.delete(id));
            }
          });
        }
        // 展开当前节点
        newSet.add(aid);
      }

      return Array.from(newSet);
    });
  }, [navData]);

  return (
    // 关掉 HoverFill 的背景共享（设计系统默认 sharable: true）：共享时它只在同级项之间平移上一项
    // 的背景（只改 width/位置），高度仍取自源元素 → 两行标题移到一行标题时，灰块还是两行高
    <ConfigProvider hoverFill={{ sharable: false }}>
      <div className="odc-docs-menu" ref={menuRef}>
        <div
          className={classNames('menu-category-getting-started', {
            active: activeId === '',
          })}
          onClick={() => {
            setPid('');
          }}
        >
          {t('Getting started')}
        </div>
        {ready ? navData.map((item, index) => (
          <ArticleMenuSub
            key={item.aid}
            activeId={activeId || ''}
            item={item}
            index={index}
            expandedIds={expandedIds}
            onToggle={handleToggle}
            setPid={setPid}
          />
        )) : (
          // 目录数据还是上一个语言的（或尚未到位）：给骨架，不渲染旧语言的名字
          <div className="menu-loading">
            <Skeleton rows={[
              { width: '100%', height: '20px' },
              { width: '82%', height: '20px' },
              { width: '91%', height: '20px' },
              { width: '70%', height: '20px' },
            ]}
            />
          </div>
        )}
      </div>
    </ConfigProvider>
  );
}